import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext } from 'src/M_Components'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow, Collapse, IconButton } from '@mui/material'
import { CheckCircle, Warning, ExpandMore, ExpandLess } from '@mui/icons-material'

export const MovementCheckDemurrage = ({ open, setOpen, selectedMovements }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)

    const [step, setStep] = useState('confirm')
    const [results, setResults] = useState([])
    const [expandedRows, setExpandedRows] = useState(new Set())

    const checkDemurrageService = new BufferService('Movement/CheckDemurrage', 'secondary')
    // Account service lives in the main API — used to resolve account GUIDs to readable codes
    const accountService = new BufferService('Account')

    const handleConfirm = async () => {
        const movementIds = selectedMovements.map(m => m.Id).filter(Boolean)

        if (movementIds.length === 0) {
            ctx.toastMessages('error', '', t("No movement IDs found"))
            return
        }

        const movementsWithDemurrage = selectedMovements.filter(m => m.DemurrageId)
        const movementsWithoutDemurrage = selectedMovements.filter(m => !m.DemurrageId)

        if (movementsWithDemurrage.length === 0) {
            ctx.toastMessages('error', '', t('None of the selected movements have an associated demurrage. Check Demurrage cannot proceed.'))
            return
        }

        if (movementsWithoutDemurrage.length > 0) {
            ctx.toastMessages('warning', '', `${movementsWithoutDemurrage.length} ${t('selected movement(s) have no demurrage and will be skipped.')}`)
        }

        const filteredMovementIds = movementsWithDemurrage.map(m => m.Id).filter(Boolean)

        ctx.setIsLoad(true)

        try {
            const response = await checkDemurrageService.post(filteredMovementIds)

            if (response?.name === 'AxiosError') {
                ctx.setIsLoad(false)
                ctx.toastMessages('error', '', t("Error checking demurrages"))
                return
            }

            // For items with a SuggestedAccountId, resolve the account code
            // so the operator sees a readable name instead of a GUID.
            // If resolution fails the account line is simply omitted — no broken display.
            const enriched = await Promise.all(response.map(async item => {
                if (!item.SuggestedAccountId || !item.WarningDetail) return item

                try {
                    const account = await accountService.getId(item.SuggestedAccountId)
                    if (account?.name !== 'AxiosError' && account?.AccountCode) {
                        return {
                            ...item,
                            WarningDetail: item.WarningDetail + `\nAccount: ${account.AccountCode}`
                        }
                    }
                } catch {
                    // Account fetch failed — leave item unchanged, GUID is not exposed
                }

                return item
            }))

            ctx.setIsLoad(false)
            setResults(enriched)
            setStep('result')

        } catch {
            ctx.setIsLoad(false)
            ctx.toastMessages('error', '', t("Error checking demurrages"))
        }
    }

    const handleClose = () => {
        setOpen(false)
        setStep('confirm')
        setResults([])
        setExpandedRows(new Set())
    }

    const toggleDetail = (movementId) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            if (next.has(movementId)) {
                next.delete(movementId)
            } else {
                next.add(movementId)
            }
            return next
        })
    }

    const warnings = results.filter(r => !r.IsValid)
    const corrections = results.filter(r => r.IsValid && r.Action !== 'OK')
    const allOk = results.length > 0 && warnings.length === 0

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ color: ctx.primaryColor, fontWeight: 'bold' }}>
                {t('Check Demurrage')}
            </DialogTitle>

            <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {step === 'confirm' && (
                    <Typography>
                        {t('This will check')} <strong>{selectedMovements?.length}</strong> {t('selected movement(s) and correct any demurrage inconsistencies. Do you want to proceed?')}
                    </Typography>
                )}

                {step === 'result' && allOk && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'green' }}>
                            <CheckCircle />
                            <Typography>{t('All demurrages are correctly set')}</Typography>
                        </div>
                        {corrections.length > 0 && (
                            <>
                                <Typography variant="body2" sx={{ color: 'grey', marginTop: '10px' }}>
                                    {t('The following corrections were made automatically:')}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>{t('Movement ID')}</strong></TableCell>
                                            <TableCell><strong>{t('Action')}</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {corrections.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.MovementId}</TableCell>
                                                <TableCell>{item.Action}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </div>
                )}

                {step === 'result' && !allOk && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'orange', marginBottom: '15px' }}>
                            <Warning />
                            <Typography>
                                {warnings.length} {t('movement(s) have demurrage issues')}
                            </Typography>
                        </div>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'var(--color-light-grey, #f5f5f5)' }}>
                                    <TableCell style={{ width: '100px' }}><strong>{t('Movement ID')}</strong></TableCell>
                                    <TableCell><strong>{t('Warning')}</strong></TableCell>
                                    <TableCell style={{ width: '40px' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {warnings.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <TableRow sx={{ backgroundColor: 'rgba(255,160,0,0.05)' }}>
                                            <TableCell>{item.MovementId}</TableCell>
                                            <TableCell>{item.Warning}</TableCell>
                                            <TableCell>
                                                {item.WarningDetail && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleDetail(item.MovementId)}
                                                        title={t('Show configuration details')}
                                                    >
                                                        {expandedRows.has(item.MovementId)
                                                            ? <ExpandLess fontSize="small" />
                                                            : <ExpandMore fontSize="small" />
                                                        }
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        {item.WarningDetail && (
                                            <TableRow>
                                                <TableCell colSpan={3} sx={{ padding: 0, border: 0 }}>
                                                    <Collapse in={expandedRows.has(item.MovementId)} timeout="auto" unmountOnExit>
                                                        <div style={{
                                                            padding: '12px 16px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderLeft: '3px solid orange',
                                                            margin: '0 8px 8px 8px',
                                                            borderRadius: '0 4px 4px 0'
                                                        }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 600, marginBottom: '8px', color: '#495057' }}
                                                            >
                                                                {t('Required MovementLink configuration:')}
                                                            </Typography>
                                                            {item.WarningDetail.split('\n').map((line, i) => (
                                                                line.trim() && (
                                                                    <Typography
                                                                        key={i}
                                                                        variant="body2"
                                                                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#495057', lineHeight: 1.8 }}
                                                                    >
                                                                        {line}
                                                                    </Typography>
                                                                )
                                                            ))}
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ marginTop: '10px', color: '#6c757d', fontStyle: 'italic' }}
                                                            >
                                                                {t('Go to Sequence Movements, create or correct a rule with these values, then run Check Demurrage again.')}
                                                            </Typography>
                                                        </div>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {step === 'confirm' && (
                    <>
                        <Button onClick={handleConfirm} variant="contained"
                            sx={{ backgroundColor: 'var(--color-green)', color: 'var(--color-white)' }}>
                            {t('Confirm')}
                        </Button>
                        <Button onClick={handleClose} variant="contained"
                            sx={{ backgroundColor: 'var(--color-red)', color: 'var(--color-white)' }}>
                            {t('Cancel')}
                        </Button>
                    </>
                )}
                {step === 'result' && (
                    <Button onClick={handleClose} variant="contained"
                        sx={{ backgroundColor: ctx.primaryColor, color: 'var(--color-white)' }}>
                        {t('Close')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}